import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useMapSettings } from '../../lib/useMapSettings';
import { BaseTiles, TrafficTiles, useMapLayers, LayerMenu } from './MapLayers';
import { useTranslation, useI18nStore } from '../../lib/i18n';
import { LEG_COLORS } from './helpers';
import { useRouteSim } from './useRouteSim';
import { SimControls } from './SimControls';
const tt = (k, p) => useI18nStore.getState().t(`startStopAdmin.${k}`, p);

export { LEG_COLORS };

const TRUCK = L.divIcon({
  className: '',
  html: '<div style="position:absolute;transform:translate(-50%,-50%);width:34px;height:34px;border-radius:999px;background:#0f172a;border:3px solid #fbbf24;box-shadow:0 3px 10px rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg></div>',
  iconSize: [0, 0],
});

const pin = (label, bg) => L.divIcon({
  className: '',
  html: `<div style="position:absolute;left:0;top:0;width:max-content;background:${bg};color:#fff;font:700 10px/1 system-ui;padding:5px 7px;border-radius:999px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);white-space:nowrap;transform:translate(-50%,-50%)">${label}</div>`,
  iconSize: [0, 0],
});
const icons = () => ({ depot: pin(tt('pinDepot'), '#0f172a'), start: pin('START', '#059669'), stop: pin('STOP', '#e11d48') });

function FitBounds({ points, trigger }) {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length === 1) map.setView(points[0], 14);
      else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 15 });
    }, 150);
    return () => clearTimeout(t);
  }, [map, points, trigger]);
  return null;
}

const CtrlBtn = ({ onClick, title, children, testId, active }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testId}
    className={`h-8 px-2.5 inline-flex items-center gap-1 text-xs font-semibold rounded-lg shadow-md border transition-colors ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}>
    {children}
  </button>
);

export function StartStopRouteMap({ data }) {
  useTranslation();
  const ICONS = useMemo(icons, [useI18nStore.getState().language]); // eslint-disable-line react-hooks/exhaustive-deps
  const wrap = useRef(null);
  const cfg = useMapSettings();
  const m = useMapLayers(cfg);
  const [full, setFull] = useState(false);
  const s = useRouteSim(data, cfg?.default_sim_speed);
  const points = useMemo(() => {
    const pts = (data.legs || []).flatMap((l) => l.geometry);
    [data.depot, data.start, data.stop].forEach((p) => p && pts.push([p.lat, p.lng]));
    return pts;
  }, [data]);
  useEffect(() => {
    const onFs = () => setFull(document.fullscreenElement === wrap.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  if (!points.length) return null;
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else if (wrap.current?.requestFullscreen) wrap.current.requestFullscreen();
    else setFull((f) => !f);
  };
  const sameDepotStart = data.depot && data.start && Math.abs(data.depot.lat - data.start.lat) < 1e-4 && Math.abs(data.depot.lng - data.start.lng) < 1e-4;
  return (
    <div className="space-y-2">
    <div ref={wrap} className={`relative rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 ${full ? 'h-screen w-screen rounded-none' : 'h-72 sm:h-80'}`} data-testid="ss-route-map">
      <MapContainer center={points[0]} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={full}>
        <BaseTiles id={m.layer} onFail={m.onFail} cfg={cfg} />
        {m.traffic && m.trafficAvailable && <TrafficTiles />}
        {(data.legs || []).map((l) => (
          <Polyline key={l.key} positions={l.geometry}
            pathOptions={{ color: LEG_COLORS[l.key], weight: 5, opacity: 0.9, dashArray: l.source === 'straight' ? '8 8' : undefined }}>
            <Tooltip sticky>{l.label}: {l.km} km</Tooltip>
          </Polyline>
        ))}
        {data.depot && <Marker position={[data.depot.lat, data.depot.lng]} icon={ICONS.depot}><Tooltip>{data.depot.name || tt('depot')}</Tooltip></Marker>}
        {data.start && !sameDepotStart && <Marker position={[data.start.lat, data.start.lng]} icon={ICONS.start}><Tooltip>{data.start.address || 'Start'}</Tooltip></Marker>}
        {data.stop && <Marker position={[data.stop.lat, data.stop.lng]} icon={ICONS.stop}><Tooltip>{data.stop.address || 'Stop'}</Tooltip></Marker>}
        {s.sim && s.t > 0 && <Polyline positions={s.trail} pathOptions={{ color: '#fbbf24', weight: 7, opacity: 0.85 }} />}
        {s.sim && <Marker position={[s.pos.lat, s.pos.lng]} icon={TRUCK} zIndexOffset={1000}><Tooltip>{s.clock}</Tooltip></Marker>}
        <FitBounds points={points} trigger={full} />
      </MapContainer>
      <div className="absolute top-2 right-2 z-[500] flex gap-1.5">
        <LayerMenu m={m} />
        <CtrlBtn onClick={toggleFull} title={full ? tt('exitFullscreen') : tt('fullscreen')} testId="ss-map-fullscreen">
          {full ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </CtrlBtn>
      </div>
      {full && s.sim && <div className="absolute bottom-3 left-3 right-3 z-[500]"><SimControls s={s} /></div>}
    </div>
    {!full && s.sim && <SimControls s={s} />}
    </div>
  );
}
